import * as React from 'react';
import _ from 'lodash';

import { RoleType } from '@tock/coretypes';
import RoleContext, { IRoleContext } from 'utils/roleContext';

export function shouldDisplayForRole(
  userRole: RoleType,
  context: IRoleContext,
  minOrMax: 'min' | 'max'
) {
  const {
    hasDriverRole,
    hasHostRole,
    hasEmployeeRole,
    hasAdminRole,
    hasAccountOwnerRole,
    hasHospitalityRole,
    hasFinanceRole,
    hasEngRole,
  } = context;
  let orderedRoles = [
    hasDriverRole,
    hasHostRole,
    hasEmployeeRole,
    hasAdminRole,
    hasAccountOwnerRole,
    hasHospitalityRole,
    hasFinanceRole,
    hasEngRole,
  ];
  if (minOrMax === 'max') {
    orderedRoles = _.reverse(orderedRoles);
  }

  const sliceAmount = {
    [RoleType.DRIVER]: minOrMax === 'min' ? 0 : 7,
    [RoleType.HOST]: minOrMax === 'min' ? 1 : 6,
    [RoleType.EMPLOYEE]: minOrMax === 'min' ? 2 : 5,
    [RoleType.ADMIN]: minOrMax === 'min' ? 3 : 4,
    [RoleType.ACCOUNT_OWNER]: minOrMax === 'min' ? 4 : 3,
    [RoleType.HOSPITALITY]: minOrMax === 'min' ? 5 : 2,
    [RoleType.FINANCE]: minOrMax === 'min' ? 6 : 1,
    [RoleType.ENG]: minOrMax === 'min' ? 7 : 0,
  };
  return _.some(_.drop(orderedRoles, sliceAmount[userRole]));
}

type RenderPropFunction = (props: { pass: boolean } & IRoleContext) => React.ReactNode;

interface RoleRestrictionProps {
  minRole?: RoleType;
  maxRole?: RoleType;
  children: React.ReactNode | RenderPropFunction;
}

const RoleRestriction: React.FunctionComponent<React.PropsWithChildren<RoleRestrictionProps>> = (
  props: RoleRestrictionProps
) => {
  const minRole = props.minRole || RoleType.DRIVER;
  const maxRole = props.maxRole || RoleType.ENG;
  const context = React.useContext(RoleContext);

  const meetsRestrictions =
    shouldDisplayForRole(minRole, context, 'min') && shouldDisplayForRole(maxRole, context, 'max');

  if (typeof props.children === 'function') {
    const renderFunc = props.children as RenderPropFunction;
    return (
      <>
        {renderFunc({
          pass: meetsRestrictions,
          ...context,
        })}
      </>
    );
  }
  if (meetsRestrictions) {
    return <>{props.children}</>;
  }
  return null;
};

export default RoleRestriction;
